/**
 * Where the sun is (T103 piece ②).
 *
 * The clock says what day it is; this says what the sky should be doing about
 * it. **D41**'s whole bet is that **3½ minutes of midwinter light against 16½ of
 * dark** feels right, and that only means anything if the light is really
 * Iceland's rather than a sine wave somebody liked the shape of.
 *
 * ## Verified against published times, not derived and hoped for
 *
 * At Reykjavík (64.14°N, 21.92°W) this reproduces **21 December: sunrise 11:22,
 * sunset 15:29 — to the minute** — and midsummer within about five minutes of
 * the published figures, which themselves disagree by more than that. See
 * `sun.test.ts`, which carries the reference values and their sources.
 *
 * ## 🔴 The clock is UTC, so noon is not midday
 *
 * Iceland keeps **UTC all year** while sitting **22° west**, so the sun is
 * highest at about **13:26**, not 12:00. That is not a bug to be corrected — it
 * is one of the genuinely odd true things about living here, and midwinter
 * sunrise really is around 11:20.
 *
 * ⚠️ **It also keeps the clock and the weather archive on one time base.** D46's
 * archived year is stamped in UTC, so *game hour 14 is observation hour 14*.
 * Shifting the game clock to put the sun overhead at noon would put a permanent
 * 1½-hour offset between the sky and the weather, and nothing would ever say so.
 */

import { DAYS_PER_YEAR, type WorldTime } from './clock.ts';

const RAD = Math.PI / 180;

/**
 * Where the world is, in degrees.
 *
 * ⚠️ **The centre of `TEST_AREA`** — the spawn at ISN93 347580, 396315, which is
 * Hraunin west of Straumsvík. **Proved by round trip in `sun.test.ts`**:
 * `latLonToIsn93` of these two numbers lands back on the spawn to within a
 * couple of centimetres, which is how they are pinned without writing the
 * inverse projection that `latlon.ts` deliberately does not have. ⚠️ **Six
 * decimal places is about 11 cm on the ground** — more digits would be invented
 * precision, and the sun does not move measurably across a hand's width.
 *
 * ⚠️ ADR-0007 quotes **64.07°N** throughout. The real centre is **64.036°N** —
 * close enough that every daylight figure in the ADR still holds, and worth
 * knowing before somebody "corrects" one of these to match the other.
 */
export const WORLD_LATITUDE = 64.036185;
export const WORLD_LONGITUDE = -22.12008;

/**
 * The sun's angle below the horizon at which it counts as risen or set.
 *
 * **−0.833°, the standard value**, and it is two effects rather than a fudge:
 * the sun is a disc about half a degree wide and the horizon touches its *top*
 * edge, and the atmosphere refracts the image up by roughly another third of a
 * degree. Together they add about **eight minutes** to a midwinter Icelandic
 * day, which is 4% of it.
 */
const HORIZON_REFRACTION = -0.833;

interface SolarTerms {
  /** Solar declination, radians. */
  readonly declination: number;
  /** The equation of time, minutes. */
  readonly equationOfTime: number;
}

/**
 * The two numbers that vary through the year.
 *
 * **Declination** is how far north or south the sun is — ±23.44° at the
 * solstices, and the entire reason the light changes at all.
 *
 * ⚠️ **The equation of time** is the one nobody expects: a sundial and a clock
 * disagree by up to **±16 minutes** depending on the date, because the Earth's
 * orbit is elliptical and its axis is tilted. Leaving it out would put sunrise
 * out by a quarter of an hour in early November — small, wrong, and exactly the
 * sort of error that is never traced back to its cause.
 *
 * Fourier fits from NOAA's solar position notes; accurate to well under a
 * minute, which is far finer than a 20-minute day can express.
 */
function solarTerms(dayOfYear: number, hoursUtc: number): SolarTerms {
  const g = ((2 * Math.PI) / DAYS_PER_YEAR) * (dayOfYear - 1 + (hoursUtc - 12) / 24);

  return {
    declination:
      0.006918 -
      0.399912 * Math.cos(g) +
      0.070257 * Math.sin(g) -
      0.006758 * Math.cos(2 * g) +
      0.000907 * Math.sin(2 * g) -
      0.002697 * Math.cos(3 * g) +
      0.00148 * Math.sin(3 * g),
    equationOfTime:
      229.18 *
      (0.000075 +
        0.001868 * Math.cos(g) -
        0.032077 * Math.sin(g) -
        0.014615 * Math.cos(2 * g) -
        0.040849 * Math.sin(2 * g)),
  };
}

/** Where the sun is in the sky. */
export interface SunPosition {
  /**
   * Degrees above the horizon. **Negative means below it**, and how far below
   * matters: −6° is civil twilight, −18° is properly dark.
   */
  readonly elevation: number;
  /** Degrees clockwise from north: 90 is east, 180 south, 270 west. */
  readonly azimuth: number;
  /**
   * A unit vector pointing **from the ground towards the sun**, in world axes —
   * `x` east, `y` up, `z` **negated northing**, so **north is −z**.
   *
   * ⚠️ The z negation is `world.ts`'s convention and the reason this is computed
   * here rather than in the renderer: getting it wrong puts the sun in the north
   * and every shadow the wrong way round, and it looks plausible enough to
   * survive a glance.
   */
  readonly direction: { readonly x: number; readonly y: number; readonly z: number };
}

/** Where the sun is at a moment of the world's clock. */
export function sunPosition(
  time: WorldTime,
  latitude = WORLD_LATITUDE,
  longitude = WORLD_LONGITUDE,
): SunPosition {
  const hoursUtc = time.dayFraction * 24;
  const { declination, equationOfTime } = solarTerms(time.dayOfYear, hoursUtc);

  /* True solar time: the clock, corrected for how far we are from the meridian
     our timezone is named after, and for the sundial's own wobble. */
  const trueSolarMinutes = hoursUtc * 60 + equationOfTime + 4 * longitude;
  const hourAngle = (trueSolarMinutes / 4 - 180) * RAD;

  const latR = latitude * RAD;
  const sinElevation =
    Math.sin(latR) * Math.sin(declination) +
    Math.cos(latR) * Math.cos(declination) * Math.cos(hourAngle);
  const elevationR = Math.asin(Math.min(1, Math.max(-1, sinElevation)));

  /* Azimuth from the same triangle. Clamped before `acos` for the same reason
     the elevation is: at exactly the pole of the expression, floating point
     produces 1.0000000000000002 and `acos` returns NaN. */
  const cosAzimuth =
    (Math.sin(declination) - Math.sin(elevationR) * Math.sin(latR)) /
    (Math.cos(elevationR) * Math.cos(latR));
  let azimuth = Math.acos(Math.min(1, Math.max(-1, cosAzimuth))) / RAD;
  /* Before local solar noon the sun is in the east, and `acos` cannot tell —
     it only ever answers 0…180. */
  if (hourAngle > 0) azimuth = 360 - azimuth;

  const elevation = elevationR / RAD;
  const horizontal = Math.cos(elevationR);

  return {
    elevation,
    azimuth,
    direction: {
      x: Math.sin(azimuth * RAD) * horizontal,
      y: Math.sin(elevationR),
      /* North is −z, so a southerly sun (azimuth 180) gives +z. */
      z: -Math.cos(azimuth * RAD) * horizontal,
    },
  };
}

/** When the sun is up on a given day. */
export interface Daylight {
  /**
   * `normal` — it rises and sets · `midnight-sun` — it never sets ·
   * `polar-night` — it never rises.
   *
   * ⚠️ **Both extremes are reachable from Iceland and neither happens here.**
   * At 64°N the sun always technically rises, even at midwinter. The cases are
   * handled because **D8 puts the whole island in the world**, and Grímsey sits
   * on the Arctic Circle — the day that ground is generated, a model that
   * divided by zero at the pole would fail there and nowhere else.
   */
  readonly kind: 'normal' | 'midnight-sun' | 'polar-night';
  /** Hours, on the world clock. Absent unless `kind` is `normal`. */
  readonly sunrise?: number;
  readonly sunset?: number;
  /** When the sun is highest. Always present — it exists even in polar night. */
  readonly solarNoon: number;
  /** Hours of daylight: 0 through polar night, 24 through midnight sun. */
  readonly hours: number;
}

/** Sunrise, sunset and solar noon for a day of the year. */
export function daylight(
  dayOfYear: number,
  latitude = WORLD_LATITUDE,
  longitude = WORLD_LONGITUDE,
): Daylight {
  const { declination, equationOfTime } = solarTerms(dayOfYear, 12);
  const latR = latitude * RAD;

  const solarNoon = (720 + 4 * -longitude - equationOfTime) / 60;

  const cosHourAngle =
    Math.cos((90 - HORIZON_REFRACTION) * RAD) / (Math.cos(latR) * Math.cos(declination)) -
    Math.tan(latR) * Math.tan(declination);

  if (cosHourAngle < -1) return { kind: 'midnight-sun', solarNoon, hours: 24 };
  if (cosHourAngle > 1) return { kind: 'polar-night', solarNoon, hours: 0 };

  const halfDayHours = Math.acos(cosHourAngle) / RAD / 15;

  return {
    kind: 'normal',
    sunrise: solarNoon - halfDayHours,
    sunset: solarNoon + halfDayHours,
    solarNoon,
    hours: halfDayHours * 2,
  };
}
